import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import CustomCheckbox from '../CustomCheckbox';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTicketsByClassTime,
  getClosedCanceledMembersByClassTime,
  addMembersToCourse,
  attendMembersToClassroomMiss,
  attendMembersToClassroom,
  attendMembersToClassroomDelete,
  getMemberAvailableTickets,
} from '../../api/ticket';
import SelectTicketModal from './SelectTicketModal';
import GroupReserveModal from './GroupReserveModal';
import ConfirmModal from '../common/modal/ConfirmModal';
import { getMemberByTicket } from '../../api/member-management';
import { FormInputSearch } from '../../form-components/FormInputSearch';
import { useToast } from 'use-toast-mui';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { errorMessages } from '../../utils/methods';
import { withOverlapConfirm } from '../../utils/overlapConfirm';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import useUserAuth from '../../utils/useUserAuth';

import moment from 'moment';
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '606px',
  height: '890px',
  bgcolor: '#fff',
  borderRadius: '10px',
  border: 'none',
};

export default function LessonUsers({ setOpens, opens, lesson, anchorEl, custom, onEventDeleted }) {
  const [users, setUsers] = useState([]);
  // 폐강으로 예약이 취소된 회원(연락용). 참석 현황 목록에는 취소가 안 보여서 따로 받는다
  const [closedCanceledUsers, setClosedCanceledUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  // 수업 상품 보유 회원 전체를 받아 와서 거르는 구조라, 다 올 때까지는 검색 결과가 비어 보인다
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberTicketData, setMemberTicketData] = useState([]);
  // 수강권 선택 모달 상태 (보유 유효 수강권이 2개 이상일 때만 사용)
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [availableTickets, setAvailableTickets] = useState([]);
  const [pendingStatus, setPendingStatus] = useState(undefined);
  // 대기→예약 전환 확인 모달
  const [waitingConfirmOpen, setWaitingConfirmOpen] = useState(false);
  // 회원 그룹 일괄 예약 서브모달
  const [groupReserveOpen, setGroupReserveOpen] = useState(false);
  const [pendingTicket, setPendingTicket] = useState(undefined);
  // 출결 처리 API 응답 전 연타로 인한 중복 차감 방지
  const [isAttending, setIsAttending] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useSelector(state => state.user);
  const queryClient = useQueryClient();
  const { handleSubmit, control, setValue, watch } = useForm();
  const eventData = custom ? anchorEl : anchorEl.extendedProps.eventData;
  const navigate = useNavigate();

  // 권한 체크
  const isPrivateLesson =
    lesson.courseClassType === 'PRIVATE_LESSON' || lesson.courseClassType === 'DUET_LESSON' || lesson.courseClassType === 'OT';
  // 여러 명 OT — 그룹 참석관리 화면을 쓰되 대기열이 없고, 전원 취소 시 수업이 삭제된다
  const isOt = lesson.courseClassType === 'OT';
  // OT·듀엣은 회원마다 자기 수강권으로 따로 예약한다 — 회원 추가·개별 출결·개별 취소, 남은 예약이 없을 때만 수업 삭제(듀엣 11/3)
  const isRoster = isOt || lesson.courseClassType === 'DUET_LESSON';
  const canAttendance = useUserAuth(isPrivateLesson ? 'SCHEDULE_PRIVATE_ATTENDANCE' : 'SCHEDULE_GROUP_ATTENDANCE');
  const canPastAttendance = useUserAuth('SCHEDULE_PRIVATE_PAST_ATTENDANCE');
  const canCancel = useUserAuth(isPrivateLesson ? 'SCHEDULE_PRIVATE_CANCEL' : 'SCHEDULE_GROUP_CANCEL');

  // 과거 수업 여부 판단 (개인레슨에서 과거 출석 추가 권��� 분리)
  // custom 은 지점 스케줄 모달이 넘기는 값 — 월·주 보기에서 클릭한 회차의 수업일은 classDate 다(registrationStartDate 는 수업 등록 시작일)
  const classDate = custom ? eventData.classDate ?? eventData.registrationStartDate : eventData.classDate;
  const isPastLesson = classDate && moment(classDate).isBefore(moment(), 'day');
  const canDoAttendance = isPrivateLesson && isPastLesson ? canPastAttendance : canAttendance;
  // Combine classDate and time
  const startDateTime = custom
    ? `${classDate}T${eventData.startTime}`
    : `${eventData.classDate}T${eventData.startTime}`;
  const endDateTime = custom
    ? `${eventData.classDate ?? eventData.registrationEndDate}T${eventData.endTime}`
    : `${eventData.classDate}T${eventData.endTime}`;

  // Format the dates
  const formattedStartDate = moment(startDateTime).format('YYYY년 M월 D일 (ddd) A h:mm');
  const formattedEndDate = moment(endDateTime).format('A h:mm');

  const location = useLocation();
  const getMembers = async (courseTickets = [], trialTickets = []) => {
    const safeCourse = Array.isArray(courseTickets) ? courseTickets : [];
    const safeTrial = Array.isArray(trialTickets) ? trialTickets : [];

    if (safeCourse.length === 0 && safeTrial.length === 0) {
      return;
    }

    setMembersLoading(true);
    try {
      const courseQ = safeCourse.map(t => {
        const id = t?.ticketId ?? t;
        return `courseTickets=${id}`;
      });
      const trialQ = safeTrial.map(t => {
        const id = t?.trialTicketId ?? t?.ticketId ?? t;
        return `trialTickets=${id}`;
      });

      const parts = [...courseQ, ...trialQ, `courseClassTimeId=${eventData?.courseClassTimeId}`];
      const q = parts.join('&');

      const data = await getMemberByTicket(user.token, q);
      const transformArray = (data ?? []).map(item => ({
        id: item.memberId,
        name: item.memberName,
      }));
      setSearchResults(transformArray);
      setMemberTicketData(data ?? []);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setMembersLoading(false);
    }
  };

  const getUsers = async id => {
    try {
      const [data, closedCanceled] = await Promise.all([
        getTicketsByClassTime(id, user.token),
        // 백엔드보다 먼저 배포돼도 참석 현황은 떠야 하므로 이 목록 실패는 빈 목록으로 본다
        getClosedCanceledMembersByClassTime(id, user.token).catch(() => []),
      ]);
      setUsers(data);
      setClosedCanceledUsers(closedCanceled ?? []);
    } catch (error) {
      console.log('error', error);
      toast.error(errorMessages(error));
    }
  };

  // 예약/대기 공통 전송. 선택한 티켓(ticket)의 ticketType에 따라 차감 대상 id를 분기한다.
  // TRIAL → memberTrialTicketId, COURSE → memberCourseTicketId. 둘 다 보내지 않는다.
  const submitReservation = async (status, ticket, convertWaiting = false) => {
    const body = { members: [memberId.id] };
    if (status) {
      body.status = status;
    }
    if (convertWaiting) {
      body.convertWaiting = true;
    }
    if (ticket?.ticketType === 'TRIAL') {
      body.memberTrialTicketId = ticket.memberTrialTicketId;
    } else if (ticket?.memberCourseTicketId != null) {
      body.memberCourseTicketId = ticket.memberCourseTicketId;
    }
    try {
      await withOverlapConfirm(force => addMembersToCourse(eventData.courseClassTimeId, body, user.token, force));
      getUsers(eventData.courseClassTimeId);
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      toast.success(t('Class created successfully'));
    } catch (error) {
      // 겹침 확인창에서 취소했으면 안내 없이 끝낸다
      if (error?.silent) return;
      const errorCode = error.response?.data?.errorCode;
      const statusCode = error.response?.status;

      if (statusCode === 409 && errorCode === 'DUPLICATE_RESOURCE') {
        toast.error(error.response.data.message);
        return;
      }

      if (statusCode === 409 && errorCode === 'WAITING_EXISTS') {
        setPendingTicket(ticket);
        setWaitingConfirmOpen(true);
        return;
      }

      toast.error(error.response?.data?.message || errorMessages(error));
    }
  };

  // 보유 유효 수강권을 먼저 조회해서, 2개 이상이면 선택 모달을 띄우고 1개면 그대로 자동 차감.
  const prepareReservation = async status => {
    if (memberId == null) {
      toast.error('회원을 선택해 주세요');
      return;
    }
    try {
      const tickets = await getMemberAvailableTickets(eventData.courseClassTimeId, memberId.id, user.token);
      if ((tickets?.length ?? 0) >= 2) {
        setAvailableTickets(tickets);
        setPendingStatus(status);
        setTicketModalOpen(true);
        return;
      }
      // 1개면 그 티켓으로, 0개면 지정 없이 진행(백엔드가 사유 메시지로 거절)
      const ticket = tickets?.length === 1 ? tickets[0] : undefined;
      await submitReservation(status, ticket);
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || errorMessages(error));
    }
  };

  const handleWaitingConvertConfirm = async () => {
    setWaitingConfirmOpen(false);
    await submitReservation(undefined, pendingTicket, true);
  };

  const handleTicketSelected = async ticket => {
    setTicketModalOpen(false);
    await submitReservation(pendingStatus, ticket);
  };

  const addMemberReservation = async () => {
    await prepareReservation(undefined);
  };

  const addMemberWaiting = async () => {
    if (memberId == null) {
      toast.error('회원을 선택해 주세요');
      return;
    }
    // 이미 WAITING 상태인 회원인지 확인
    const isAlreadyWaiting = users.some(user => user.memberId === memberId.id && user.status === 'WAITING');

    if (isAlreadyWaiting) {
      toast.error('이미 대기 목록에 추가된 회원입니다');
      return;
    }

    await prepareReservation('WAITING');
  };

  const handleChange = e => {
    const { name, checked } = e.target;

    let updatedUsers = [];

    if (name === 'allSelectReserved') {
      // RESERVED, ATTENDED, ABSENT 상태의 회원을 체크/해제
      updatedUsers = users.map(user => {
        if (user.status === 'RESERVED' || user.status === 'ATTENDED' || user.status === 'ABSENT') {
          return { ...user, isChecked: checked };
        }
        return user;
      });
    } else if (name === 'allSelectWaiting') {
      // 대기(WAITING) 상태인 users만 체크/해제
      updatedUsers = users.map(user => {
        if (user.status === 'WAITING') {
          return { ...user, isChecked: checked };
        }
        return user;
      });
    } else {
      updatedUsers = users.map(user =>
        user.memberName + user.memberId === name ? { ...user, isChecked: checked } : user,
      );
    }

    setUsers(updatedUsers);
  };

  const membersAttendance = async attendType => {
    if (isAttending) return;
    const body = {
      memberReservationIDs: users.filter(user => user.isChecked).map(user => user.memberReservationId),
    };
    if (body.memberReservationIDs.length > 0) {
      setIsAttending(true);
      try {
        if (attendType) {
          await attendMembersToClassroom(eventData.courseClassTimeId, body, user.token);
          getUsers(eventData.courseClassTimeId);
          queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
          toast.success(t('출석 처리되었습니다'));
        } else {
          await attendMembersToClassroomMiss(eventData.courseClassTimeId, body, user.token);
          getUsers(eventData.courseClassTimeId);
          queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
          toast.success(t('결석 처리되었습니다'));
        }
      } catch (error) {
        toast.error(errorMessages(error));
      } finally {
        setIsAttending(false);
      }
    } else {
      toast.error('사용자를 선택 하세요');
    }
  };

  // 예약/대기 취소는 되돌릴 수 없어 실행 전에 한 번 확인받는다.
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const checkedCancelCount = users.filter(u => u.isChecked).length;
  const requestCancel = () => {
    if (checkedCancelCount === 0) { toast.error(t('취소할 회원을 선택해 주세요')); return; }
    setCancelConfirmOpen(true);
  };

  const deleteMembersAttendance = async () => {
    setCancelConfirmOpen(false);
    const body = {
      memberReservationIDs: users
        .filter(user => user.isChecked) // Filter only the users with isChecked set to true
        .map(user => user.memberReservationId), // Extract the memberId of each selected user
    };
    if (body.memberReservationIDs.length > 0) {
      try {
        await attendMembersToClassroomDelete(eventData.courseClassTimeId, body, user.token);

        // OT: 남은 예약이 없으면 백엔드가 수업을 지우므로 목록을 재조회하지 않는다(404 방지)
        const otDeleted = isRoster && users.filter(u => !u.isChecked && u.status !== 'CANCELED').length === 0;
        if (eventData.courseClassType !== 'PRIVATE_LESSON') {
          if (!otDeleted) getUsers(eventData.courseClassTimeId);
          queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
        }
        // OT: 수업이 삭제됐으면 모달을 닫고, 어느 경우든 스케줄을 다시 불러온다
        if (isRoster) {
          if (otDeleted) setOpens({ ...opens, open1: false });
          onEventDeleted?.(eventData.courseClassTimeId);
        }
        toast.success(t('예약 취소를 완료하였습니다'));

        if (eventData.courseClassType === 'PRIVATE_LESSON') {
          setOpens({
            ...opens,
            open1: false,
            open2: false,
            open3: false,
            open: false,
            openMenu1: false,
            openMenu2: false,
          });

          queryClient.invalidateQueries({ queryKey: ['groupLessons'] });

          // 부모 컴포넌트에 이벤트 삭제 알림
          if (onEventDeleted) {
            onEventDeleted(eventData.courseClassTimeId);
          }
        }
      } catch (error) {
        toast.error(errorMessages(error));
      }
    } else {
      toast.error('사용자를 선택 하세요');
    }
  };

  useEffect(() => {
    const courseTickets = lesson?.tickets ?? [];
    const trialTickets = lesson?.trialTicketList ?? [];

    if (courseTickets.length === 0 && trialTickets.length === 0) {
      return;
    }

    getMembers(courseTickets, trialTickets);
  }, [lesson]);

  useEffect(() => {
    if (eventData.courseClassTimeId) {
      getUsers(eventData.courseClassTimeId);
    }
  }, [eventData.courseClassTimeId]);

  const memberId = watch('students');
  const closeModal = () => {
    setOpens({
      ...opens,
      open1: false,
      open2: false,
      open3: false,
      open: false,
      openMenu1: false,
      openMenu2: false,
    });
    // OT 는 개인 스케줄에서 열리므로 닫을 때 스케줄을 다시 불러와 추가·출결 결과를 카드에 반영한다
    if (isRoster) onEventDeleted?.(eventData.courseClassTimeId);
  };

  return (
    <>
    <Modal
      open={opens.open1}
      onClose={closeModal}>
      <Box
        sx={{
          ...style,
          height:
            lesson.courseClassType === 'PRIVATE_LESSON' ? '530px' : '890px', // 조건부 높이 적용
          // 작은 화면에서도 하단 버튼까지 닿도록 화면 높이로 제한하고 본문을 스크롤한다
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            backgroundColor: '#111',
            display: 'flex',
            justifyContent: 'space-between',
            height: '52px',
            p: '15px 20px',
            borderTopRightRadius: '10px',
            borderTopLeftRadius: '10px',
            flexShrink: 0,
          }}>
          <Typography
            sx={{
              color: '#fff',
              backgroundColor: '#111',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            {t('Attendance Management')}
          </Typography>
          <CloseIcon
            onClick={closeModal}
            sx={{ color: '#fff', cursor: 'pointer' }}
          />
        </Box>
        <Box sx={{ p: '30px 28px 0px 28px', flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
            {formattedStartDate} ~ {formattedEndDate}
          </Typography>
          {eventData?.memberPhoneNumber && (
            <Typography sx={{ fontSize: '13px', color: '#666', mt: '4px' }}>
              연락처: {eventData.memberPhoneNumber}
            </Typography>
          )}

          {/* 개인레슨이 아닐 때만 회원 검색 UI 표시(듀엣은 1명으로 만든 뒤 1명을 더 넣는다) */}
          {lesson.courseClassType !== 'PRIVATE_LESSON' && (
            <>
              <Box sx={{ display: 'flex', pt: '30px' }}>
                <Typography sx={{ mr: '28px', fontWeight: 500, alignSelf: 'center' }}>회원 검색</Typography>
                <FormInputSearch
                  control={control}
                  name="students"
                  setValue={setValue}
                  sx={{ width: '470px' }}
                  options={searchResults}
                  placeholder={t('Type the client’s name')}
                  noOptionsText="검색 결과가 없습니다"
                  loading={membersLoading}
                  loadingText="회원 목록을 불러오는 중..."
                />
              </Box>
              <hr style={{ border: '1px solid #e1e1e1' }} />
              {(lesson.courseClassType === 'GROUP_LESSON' || isRoster) && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{ display: 'flex' }}>
                    {/* OT 는 대기열이 없어 대기추가를 숨긴다 */}
                    {!isRoster && (
                      <Button
                        size="small"
                        onClick={() => addMemberWaiting()}
                        sx={{
                          fontSize: '13px',
                          height: '36px',
                          color: '#707070',
                          background: '#FFF',
                          p: '5px 20px',
                          borderRadius: '10px',
                          border: '1px solid #707070',
                        }}>
                        {t('Add Waiting')}
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={() => addMemberReservation()}
                      sx={{
                        '&:hover': { backgroundColor: '#707070' },
                        p: '5px 20px',
                        fontSize: '13px',
                        height: '36px',
                        color: '#fff',
                        background: '#707070',
                        borderRadius: '10px',
                        ml: 1,
                      }}>
                      {t('Add Reservation')}
                    </Button>
                    {/* 한 명씩 넣는 버튼 옆에 둬야 '여러 명을 한 번에'가 같은 맥락으로 읽힌다.
                        예약을 만드는 일이므로 참석관리와 같은 권한으로 잠근다. */}
{!isRoster && (
                    <Tooltip title={!canAttendance ? t('권한이 없습니다') : ''} arrow>
                      <span>
                    <Button
                      size="small"
                      disabled={!canAttendance}
                      onClick={() => setGroupReserveOpen(true)}
                      sx={{
                        fontSize: '13px',
                        height: '36px',
                        color: '#0F7F85',
                        background: '#FFF',
                        p: '5px 16px',
                        borderRadius: '10px',
                        border: '1px solid #0F7F85',
                        ml: 1,
                        '&:hover': { background: 'rgba(15, 127, 133, 0.06)' },
                      }}>
                      {t('일괄 예약')}
                    </Button>
                      </span>
                    </Tooltip>
)}
                    </Box>
                  </Box>
                )}
            </>
          )}

          {/* 예약 현황 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography sx={{ fontWeight: 500 }}>{t('Attendance Status')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography sx={{ fontWeight: 500, color: '#0F7F85' }}>
                {
                  users?.filter(user => user.status === 'RESERVED' || user.status === 'ATTENDED')?.length
                }
                {t('person')}
              </Typography>
              /
              <Typography sx={{ fontWeight: 500, color: '#666666' }}>
                {lesson.membersMaxCount}
                {t('person')}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              background: '#F1F4F6',
              borderRadius: '4px',
              p: 1,
              mt: '12px',
            }}>
            <Button
              variant="outlined"
              sx={{
                height: '36px',
                width: '130px',
                background: '#fff',
                '&:hover': { backgroundColor: '#fff' },
                borderRadius: '18px',
                border: '1.5px solid #0F7F85',
                color: '#0F7F85',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              예약 : {users?.filter(user => user.status === 'RESERVED')?.length}명
            </Button>
            <Button
              variant="contained"
              sx={{
                height: '36px',
                width: '130px',
                '&:hover': { backgroundColor: '#0F7F85' },
                borderRadius: '18px',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              출석: {users?.filter(user => user.status === 'ATTENDED')?.length}명{' '}
            </Button>
            <Button
              variant="contained"
              color="info"
              sx={{
                height: '36px',
                width: '130px',
                '&:hover': { backgroundColor: '#9A9A9A' },
                borderRadius: '18px',
                color: '#fff',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              결석: {users?.filter(user => user.status === 'ABSENT')?.length}명
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', pt: '6px' }}>
            <TableContainer sx={{ height: '550px, width:"360px', borderRadius: '4px' }}>
              <Table
                aria-label="simple table"
                sx={{
                  borderRadius: '5px',
                  border: '1px solid #ebebeb',

                  '.css-1krcoiz-MuiTableCell-root': { padding: '1px' },
                  '.css-am7boh-MuiTableCell-root': { padding: '1px' },
                  '.css-7tokan-MuiTableCell-root': { padding: '1px' },
                }}>
                <TableHead sx={{ background: '#FAFBFC' }}>
                  <TableRow sx={{ height: '40px' }}>
                    <TableCell align="center" sx={{ width: '50px', borderRight: '1px solid #ddd', padding: '8px' }}>
                      <CustomCheckbox
                        color="primary"
                        name="allSelectReserved"
                        checked={
                          !users
                            ?.filter(
                              user =>
                                user.status === 'RESERVED' || user.status === 'ATTENDED' || user.status === 'ABSENT',
                            )
                            ?.some(user => user.isChecked !== true)
                        }
                        onChange={handleChange}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: '120px',
                        borderRight: '1px solid #ddd',
                        fontWeight: 500,
                        fontSize: '12px',
                        padding: '8px',
                      }}>
                      회원명
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: '130px',
                        borderRight: '1px solid #ddd',
                        fontWeight: 500,
                        fontSize: '12px',
                        padding: '8px',
                      }}>
                      전화번호
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: '150px',
                        borderRight: '1px solid #ddd',
                        fontWeight: 500,
                        fontSize: '12px',
                        padding: '8px',
                      }}>
                      예약 일시
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: '100px',
                        borderRight: '1px solid #ddd',
                        fontWeight: 500,
                        fontSize: '12px',
                        padding: '8px',
                      }}>
                      세션
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '120px',
                        fontWeight: 500,
                        fontSize: '12px',
                        padding: '8px',
                      }}
                      align="center">
                      상태
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users?.filter(
                    user => user.status === 'RESERVED' || user.status === 'ATTENDED' || user.status === 'ABSENT',
                  )?.length > 0 &&
                    users
                      .filter(
                        user => user.status === 'RESERVED' || user.status === 'ATTENDED' || user.status === 'ABSENT',
                      )
                      .map(row => (
                        <TableRow
                          key={row.memberId}
                          sx={{
                            height: '40px',
                            '&:last-child td, &:last-child th': {
                              borderBottom: 0,
                            },
                          }}
                          onClick={() => {
                            navigate(`/member/${row.memberId}`);
                          }}>
                          <TableCell
                            align="center"
                            sx={{ width: '50px', borderRight: '1px solid #ddd', padding: '8px' }}
                            onClick={e => e.stopPropagation()}>
                            <CustomCheckbox
                              name={row.memberName + row.memberId}
                              checked={row?.isChecked || false}
                              onChange={handleChange}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: '120px', borderRight: '1px solid #ddd', padding: '8px', cursor: 'pointer' }}
                            component="th"
                            scope="row">
                            {row.memberName}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: '130px', minWidth: '130px', whiteSpace: 'nowrap', borderRight: '1px solid #ddd', padding: '8px', cursor: 'pointer' }}>
                            {row.phoneNumber}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: '150px', borderRight: '1px solid #ddd', padding: '8px', cursor: 'pointer' }}>
                            {row.registeredDate}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ width: '100px', borderRight: '1px solid #ddd', padding: '8px', cursor: 'pointer', fontSize: '12px' }}>
                            {row.remainingCount != null && row.totalCount != null
                              ? `${row.remainingCount}/${row.totalCount}`
                              : '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ width: '120px', padding: '8px', cursor: 'pointer' }}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                px: '12px',
                                py: '4px',
                                borderRadius: '4px',
                                fontSize: '13px',
                                fontWeight: 600,
                                width: '75px',
                                border:
                                  row.status === 'ATTENDED'
                                    ? '1px solid #0F7F85'
                                    : row.status === 'ABSENT'
                                      ? '1px solid #FF3B30'
                                      : row.status === 'RESERVED'
                                        ? '1px solid #007FFF'
                                        : row.status === 'CANCELED'
                                          ? '1px solid #D9D9D9'
                                          : '1px solid #d9d9d9',
                                color:
                                  row.status === 'ATTENDED'
                                    ? '#0F7F85'
                                    : row.status === 'ABSENT'
                                      ? '#FF3B30'
                                      : row.status === 'RESERVED'
                                        ? '#007FFF'
                                        : row.status === 'CANCELED'
                                          ? '#D9D9D9'
                                          : '#666',
                              }}>
                              {row.status === 'ATTENDED'
                                ? '출석 완료'
                                : row.status === 'ABSENT'
                                  ? '결석 처리'
                                  : row.status === 'RESERVED'
                                    ? '예약 완료'
                                    : row.status === 'CANCELED'
                                      ? '예약 취소'
                                      : '-'}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* 폐강으로 취소된 회원 — 연락용으로 이름·연락처를 남긴다 */}
          {closedCanceledUsers.length > 0 && (
            <Box sx={{ mt: '32px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Typography sx={{ fontWeight: 500 }}>폐강으로 취소된 회원</Typography>
                <Typography sx={{ fontWeight: 500, color: '#5C5C5C' }}>{closedCanceledUsers.length}명</Typography>
              </Box>
              <Typography sx={{ fontSize: '12px', color: '#8C8C8C', mt: '4px' }}>
                최소 인원 미달로 폐강되어 예약이 자동 취소된 회원입니다. 회원앱으로 폐강 안내가 발송됩니다.
              </Typography>
              <TableContainer sx={{ mt: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#F5F5F5' }}>
                      <TableCell align="center" sx={{ fontSize: '12px', padding: '8px', borderRight: '1px solid #ddd' }}>
                        회원명
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', padding: '8px', borderRight: '1px solid #ddd' }}>
                        전화번호
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', padding: '8px' }}>
                        예약 일시
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {closedCanceledUsers.map(row => (
                      <TableRow key={row.memberReservationId}>
                        <TableCell align="center" sx={{ padding: '8px', borderRight: '1px solid #ddd' }}>
                          {row.memberName}
                        </TableCell>
                        <TableCell align="center" sx={{ padding: '8px', borderRight: '1px solid #ddd' }}>
                          {row.phoneNumber}
                        </TableCell>
                        <TableCell align="center" sx={{ padding: '8px' }}>
                          {row.registeredDate}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* 대기 현황 (OT 는 대기열 없음) */}
          {lesson.courseClassType !== 'PRIVATE_LESSON' && lesson.courseClassType !== 'DUET_LESSON' && !isOt && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '32px' }}>
                <Typography sx={{ fontWeight: 500 }}>{t('Waiting Status')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Typography sx={{ fontWeight: 500, color: '#0F7F85' }}>
                    {users?.filter(user => user.status === 'WAITING')?.length} {t('person')}
                  </Typography>
                  /
                  <Typography sx={{ fontWeight: 500, color: '#666666' }}>
                    {lesson.membersWaitMaxCount} {t('person')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', pt: '6px' }}>
                <TableContainer sx={{ height: '550px, width:"360px', borderRadius: '4px' }}>
                  <Table
                    aria-label="simple table"
                    sx={{
                      borderRadius: '5px',
                      border: '1px solid #ebebeb',

                      '.css-1krcoiz-MuiTableCell-root': { padding: '1px' },
                      '.css-am7boh-MuiTableCell-root': { padding: '1px' },
                      '.css-7tokan-MuiTableCell-root': { padding: '1px' },
                    }}>
                    <TableHead sx={{ background: '#FAFBFC' }}>
                      <TableRow sx={{ height: '40px' }}>
                        <TableCell align="center" sx={{ width: '50px', borderRight: '1px solid #ddd', padding: '8px' }}>
                          <CustomCheckbox
                            color="primary"
                            name="allSelectWaiting"
                            checked={
                              !users?.filter(user => user.status === 'WAITING')?.some(user => user.isChecked !== true)
                            }
                            onChange={handleChange}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '120px',
                            borderRight: '1px solid #ddd',
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '8px',
                          }}>
                          회원명
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '130px',
                            borderRight: '1px solid #ddd',
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '8px',
                          }}>
                          전화번호
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '150px',
                            borderRight: '1px solid #ddd',
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '8px',
                          }}>
                          대기일시
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '100px',
                            borderRight: '1px solid #ddd',
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '8px',
                          }}>
                          세션
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '120px',
                            fontWeight: 500,
                            fontSize: '12px',
                            padding: '8px',
                          }}
                          align="center">
                          대기순번
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users?.filter(user => user.status === 'WAITING')?.length > 0 &&
                        users
                          .filter(user => user.status === 'WAITING')
                          .map((row, index) => (
                            <TableRow
                              key={row.memberId}
                              sx={{
                                height: '40px',
                                cursor: 'pointer',
                                '&:last-child td, &:last-child th': {
                                  borderBottom: 0,
                                },
                              }}
                              onClick={() => {
                                navigate(`/member/${row.memberId}`);
                              }}>
                              <TableCell
                                align="center"
                                sx={{ width: '50px', borderRight: '1px solid #ddd', padding: '8px' }}
                                onClick={e => e.stopPropagation()}>
                                <CustomCheckbox
                                  name={row.memberName + row.memberId}
                                  checked={row?.isChecked || false}
                                  onChange={handleChange}
                                />
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ width: '120px', borderRight: '1px solid #ddd', padding: '8px' }}
                                component="th"
                                scope="row">
                                {row.memberName}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ width: '130px', minWidth: '130px', whiteSpace: 'nowrap', borderRight: '1px solid #ddd', padding: '8px' }}>
                                {row.phoneNumber}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ width: '150px', borderRight: '1px solid #ddd', padding: '8px' }}>
                                {row.registeredDate}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ width: '100px', borderRight: '1px solid #ddd', padding: '8px', fontSize: '12px' }}>
                                {row.remainingCount != null && row.totalCount != null
                                  ? `${row.remainingCount}/${row.totalCount}`
                                  : '-'}
                              </TableCell>
                              <TableCell align="center" sx={{ width: '120px', padding: '8px' }}>
                                {index + 1}
                              </TableCell>
                              {/* <TableCell align="center" sx={{ width: '120px', padding: '8px' }}>
                                {(() => {
                                  // memberTicketData에서 해당 회원의 usageCount 찾기
                                  const memberTicket = memberTicketData.find(
                                    ticket => ticket.memberId === row.memberId,
                                  );
                                  return memberTicket?.usageCount ?? row?.usageCount ?? '-';
                                })()}
                              </TableCell> */}
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              mt: 'auto',
              pt: '20px',
              pb: '20px',
              position: 'sticky',
              bottom: 0,
              backgroundColor: '#fff',
              zIndex: 10,
              borderTop: '1px solid #e1e1e1',
            }}>
            {(location.pathname === '/group' || location.pathname === '/private') && (
              <Tooltip title={!canCancel ? '권한이 없습니다' : ''} arrow>
                <Button
                  size="small"
                  sx={{
                    width: '153px',
                    height: '36px',
                    color: '#fff',
                    font: 'normal normal normal 13px/15px Pretendard',
                    borderRadius: '10px',
                    '&:hover': { backgroundColor: '#707070' },
                    background: '#707070',
                    ...(!canCancel ? { opacity: 0.5, cursor: 'default' } : {}),
                  }}
                  onClick={canCancel ? requestCancel : undefined}>
                  {t('Reservation and Waiting Cancellation')}
                </Button>
              </Tooltip>
            )}
            <Tooltip title={!canDoAttendance ? '권한이 없습니다' : ''} arrow>
              <Button
                size="small"
                disabled={isAttending}
                onClick={canDoAttendance ? () => { membersAttendance(false); } : undefined}
                sx={{
                  width: '88px',
                  height: '36px',
                  color: '#fff',
                  font: 'normal normal normal 13px/15px Pretendard',
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: '#9a9a9a' },
                  background: '#DF2323',
                  ...(!canDoAttendance ? { opacity: 0.5, cursor: 'default' } : {}),
                }}>
                결석 처리
              </Button>
            </Tooltip>
            <Tooltip title={!canDoAttendance ? '권한이 없습니다' : ''} arrow>
              <Button
                onClick={canDoAttendance ? () => { membersAttendance(true); } : undefined}
                size="small"
                disabled={isAttending}
                sx={{
                  backgroundColor: '#0F7F85',
                  color: '#fff',
                  font: 'normal normal normal 13px/15px Pretendard',
                  width: '88px',
                  height: '36px',
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: '#0F7F85' },
                  ...(!canDoAttendance ? { opacity: 0.5, cursor: 'default' } : {}),
                }}>
                출석 처리
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Modal>

      {/* 이 수업 일괄 예약 — 새 피커(검색·수강권·그룹) + 일괄예약 API, 회원별 판정 표시 */}
      <GroupReserveModal
        open={groupReserveOpen}
        onClose={() => setGroupReserveOpen(false)}
        courseClassTimeId={eventData.courseClassTimeId}
        lesson={lesson}
        currentUsers={users}
        onCompleted={() => {
          getUsers(eventData.courseClassTimeId);
          queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
        }}
      />

      <SelectTicketModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        memberName={memberId?.name}
        tickets={availableTickets}
        onConfirm={handleTicketSelected}
      />

      <ConfirmModal
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={deleteMembersAttendance}
        message={`선택한 ${checkedCancelCount}명의 예약을 취소할까요?`}
        highlightText={`${checkedCancelCount}명`}
        highlightColor="#C2403D"
        cancelText="닫기"
        confirmText="예약 취소"
        confirmColor="#C2403D"
      />

      <ConfirmModal
        open={waitingConfirmOpen}
        onClose={() => setWaitingConfirmOpen(false)}
        onConfirm={handleWaitingConvertConfirm}
        message={'이미 대기 중인 회원입니다.\n대기를 취소하고 예약으로 전환하시겠습니까?'}
        highlightText="예약으로 전환"
        highlightColor="#0F7F85"
        cancelText="취소"
        confirmText="전환"
        confirmColor="#0F7F85"
      />
    </>
  );
}
